import { useEffect, useState, useCallback, type ReactNode } from 'react';
import { Sidebar } from './Sidebar';
import { MenuUsuario } from './MenuUsuario';
import { api } from '../api/client';

function useMlSyncStatus() {
  const [status, setStatus] = useState<{ lastSyncAt: string | null; durationMs: number | null; error: string | null } | null>(null);

  const poll = useCallback(async () => {
    try {
      const data = await api.get<{ lastSyncAt: string | null; durationMs: number | null; error: string | null }>('/api/v3/ml/precio-sync/status');
      setStatus(data);
    } catch {
      // silencioso
    }
  }, []);

  useEffect(() => {
    poll();
    const id = setInterval(poll, 30_000);
    return () => clearInterval(id);
  }, [poll]);

  return status;
}

type SyncStatus = { lastSyncAt: string | null; durationMs: number | null; error: string | null } | null;

function getSyncInfo(status: SyncStatus) {
  const lastSync = status?.lastSyncAt ? new Date(status.lastSyncAt) : null;
  const secsAgo = lastSync ? Math.floor((Date.now() - lastSync.getTime()) / 1000) : null;
  const stale = secsAgo !== null && secsAgo > 180;
  const label = secsAgo === null
    ? 'ML: sin sync'
    : secsAgo < 60
    ? `ML: hace ${secsAgo}s`
    : `ML: hace ${Math.floor(secsAgo / 60)}m`;
  return { stale, label };
}

function MlSyncChip({ status }: { status: SyncStatus }) {
  const { stale, label } = getSyncInfo(status);
  if (!status) return null;
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${stale ? 'bg-yellow-100 text-yellow-700' : 'bg-emerald-50 text-emerald-600'}`}>
      {label}
    </span>
  );
}

function MlStaleBanner({ status }: { status: SyncStatus }) {
  const { stale } = getSyncInfo(status);
  if (!stale) return null;
  return (
    <div className="bg-yellow-400 text-yellow-900 text-xs text-center py-0.5 font-medium shrink-0">
      Sync ML sin datos por más de 3 min — verificar conexión
    </div>
  );
}

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const syncStatus = useMlSyncStatus();

  // Lock page scroll behind the drawer/scrim while the mobile nav is open.
  useEffect(() => {
    const root = document.documentElement;
    if (mobileNavOpen) root.classList.add('overflow-hidden');
    else root.classList.remove('overflow-hidden');
    return () => root.classList.remove('overflow-hidden');
  }, [mobileNavOpen]);

  // Escape closes the drawer, same interaction shape as Modal.tsx.
  useEffect(() => {
    if (!mobileNavOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setMobileNavOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [mobileNavOpen]);

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar mobileOpen={mobileNavOpen} onMobileClose={() => setMobileNavOpen(false)} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <MlStaleBanner status={syncStatus} />
        <header className="bg-white border-b border-slate-100 px-3 md:px-6 py-2.5 flex flex-wrap items-center justify-between gap-y-2 shrink-0 shadow-sm">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setMobileNavOpen(true)}
              aria-label="Abrir menú"
              className="md:hidden p-2 -ml-2 text-slate-400 hover:text-slate-600 rounded transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div className="flex items-center gap-1.5 text-xs text-slate-400">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              <span>Conectado</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <MlSyncChip status={syncStatus} />
            <MenuUsuario />
            <button
              onClick={() => window.location.reload()}
              title="Recargar la aplicación"
              className="text-xs bg-brand-50 text-brand-600 px-3 py-1.5 rounded-full hover:bg-brand-100 transition-colors font-medium"
            >
              Actualizar
            </button>
          </div>
        </header>
        <main className="flex-1 overflow-auto p-3 md:p-6 bg-slate-50 relative">
          {/* Marca de agua — solo el perro, sin el texto "StockeArte" */}
          <div
            aria-hidden="true"
            className="pointer-events-none select-none absolute inset-0 flex items-center justify-center"
            style={{ zIndex: 0 }}
          >
            {/* El logo tiene perro en la parte superior (≈73% de altura) y texto abajo.
                Cortamos el contenedor para mostrar solo el perro. */}
            {/* "solo dobby.png" es solo el perro, sin texto — no requiere clip */}
            <img
              src="/erp/dobby.png"
              alt=""
              style={{ width: 520, height: 'auto', opacity: 0.08 }}
            />
          </div>
          <div className="relative" style={{ zIndex: 1 }}>
            {children}
          </div>
        </main>
        <footer className="hidden md:flex bg-white border-t border-slate-100 px-6 py-2 text-xs text-slate-300 items-center justify-between shrink-0">
          <span>StockeArte ERP</span>
          <span>v0.1.0</span>
        </footer>
      </div>
    </div>
  );
}
